import { PackageProviderName, SettingKey } from 'data/ppoints/constants';
import { usePPointsFeatures } from 'hooks/ppoints/usePPointsFeatures';
import { usePackageProviders } from 'hooks/usePackageProviders';
import useSettings from 'hooks/useSettings';
import { sample } from 'lodash-es';
import { ProviderToCountrySetting } from 'models/ppoints/settings/SettingsToEcom';
import { useMemo } from 'react';
import { PackageProviderFeature } from '../../../../../../../pb/responses/v1/package_provider_features_response_pb';

export interface PrimaryProvider {
    provider?: PackageProviderName;
    /** Country the provider is activated in — the provider settings route is keyed on it. */
    country?: string;
}

/**
 * The provider the dashboard tiles speak about — its logo goes on the tile art, its name into the
 * tile titles, and its country completes the link into the provider settings page.
 *
 * `provider_to_country` maps country name -> activated providers, so a shop can have several. The
 * tiles have room for one, picked at random from those that can generate labels — that is what most
 * of the tiles are about — or from all activated providers when none of them can.
 *
 * The pick is re-rolled whenever the activated set or the features response changes, so it is not
 * pinned for the lifetime of the session.
 *
 * Returns empty fields while settings load or when nothing is activated yet, which is exactly when
 * the tiles should show no logo at all.
 */
export const usePrimaryProvider = (): PrimaryProvider => {
    const { settings: providerToCountry } = useSettings<ProviderToCountrySetting>(SettingKey.provider_to_country);
    const { packageProviders } = usePackageProviders();

    /** Every activated provider paired with its country, in country order. */
    const activated = useMemo(
        () =>
            Object.entries(providerToCountry ?? {}).flatMap(([country, providers]) =>
                (providers ?? []).filter(Boolean).map((provider) => ({ provider, country }))
            ),
        [providerToCountry]
    );

    const providerIds = useMemo(() => {
        const idsByName = new Map(packageProviders?.map((p) => [p.name, p.id]));

        return [
            ...new Set(
                activated.map(({ provider }) => idsByName.get(provider)).filter((id): id is number => id !== undefined)
            ),
        ];
    }, [activated, packageProviders]);

    const { data: features } = usePPointsFeatures(providerIds);

    /**
     * Names of the activated providers that can generate labels.
     *
     * The response carries the id as a bigint, which never equals a `PackageProvider.id` number
     * under a strict compare, so it is narrowed before matching.
     */
    const labelProviderNames = useMemo(() => {
        if (!features?.data || !packageProviders) return undefined;

        const nameById = new Map(packageProviders.map((p) => [p.id, p.name]));

        return new Set(
            features.data
                .filter((entry) => entry.features.includes(PackageProviderFeature.LABEL))
                .map((entry) => nameById.get(Number(entry.packageProviderId)))
                .filter(Boolean)
        );
    }, [features, packageProviders]);

    return useMemo(() => {
        if (!activated.length) return {};

        // Label-capable providers are what most of the tiles are about, so the pick comes from
        // those. Falls back to the full list when none of them can generate labels — and while the
        // features request is still in flight, when there is nothing to filter on yet.
        const labelCapable = labelProviderNames
            ? activated.filter(({ provider }) => labelProviderNames.has(provider))
            : [];

        const pick = sample(labelCapable.length ? labelCapable : activated);

        return { provider: pick.provider, country: pick.country };
    }, [activated, labelProviderNames]);
};
